import type { BlogPost } from './types'

export const PHOTOVOLTAIK_WAERMEPUMPE_KOMBINIEREN: BlogPost = {
  slug: 'photovoltaik-waermepumpe-kombinieren',
  lang: 'de',
  topicId: 'photovoltaik-waermepumpe-kombinieren',
  title: 'Photovoltaik und Wärmepumpe kombinieren: Das bringt es wirklich',
  metaDescription:
    'Warum Photovoltaik und Wärmepumpe sich ergänzen, was das für Ihre Stromkosten bedeutet und ' +
    'worauf Sie bei der Dimensionierung achten sollten. Stand: Juli 2026.',
  keywords: [
    'Photovoltaik Wärmepumpe kombinieren',
    'Wärmepumpe mit Solarstrom betreiben',
    'PV Wärmepumpe Eigenverbrauch',
    'Wärmepumpe Angebot vergleichen',
    'Solaranlage und Wärmepumpe Kosten',
  ],
  publishedAt: '2026-07-21',
  excerpt:
    'Solaranlage und Wärmepumpe werden oft getrennt betrachtet - dabei verstärken sie sich ' +
    'gegenseitig: Die Wärmepumpe wird zum größten Stromverbraucher im Haus, die PV-Anlage liefert ' +
    'genau dafür günstigen Eigenstrom. Was das für Planung, Dimensionierung und Kosten bedeutet.',
  sections: [
    {
      type: 'paragraph',
      text:
        'Photovoltaik und Wärmepumpe werden bei der Anfrage häufig getrennt behandelt - erst die ' +
        'Solaranlage, später (oder unabhängig davon) die neue Heizung. Fachlich gehören beide ' +
        'Themen aber enger zusammen, als es diese getrennte Planung nahelegt: Eine Wärmepumpe ist ' +
        'mit Abstand der größte Stromverbraucher in einem Einfamilienhaus, deutlich vor Kühlschrank, ' +
        'Waschmaschine oder Beleuchtung zusammen. Eine Photovoltaikanlage liefert genau die Ressource, ' +
        'die eine Wärmepumpe braucht: Strom. Wer beides kombiniert plant, statt beides isoliert zu ' +
        'betrachten, trifft am Ende wirtschaftlich bessere Entscheidungen.',
    },
    {
      type: 'heading',
      text: 'Warum Photovoltaik und Wärmepumpe sich ergänzen',
    },
    {
      type: 'paragraph',
      text:
        'Eine Wärmepumpe wandelt Strom in ein Vielfaches an Wärme um (Wirkungsgrad, gemessen als ' +
        'Jahresarbeitszahl). Ob diese Energie aus dem Netz zum aktuellen Strompreis oder aus der ' +
        'eigenen Solaranlage kommt, ändert nichts an der Heizleistung - wohl aber deutlich an den ' +
        'Kosten pro Kilowattstunde Wärme. Gleichzeitig ist die Wärmepumpe ein großer, relativ ' +
        'flexibler Verbraucher: Warmwasserbereitung und - bei ausreichend dimensioniertem ' +
        'Pufferspeicher - auch ein Teil der Heizleistung lassen sich in die sonnenreichen Stunden ' +
        'des Tages verschieben, statt starr nach Bedarf zu laufen. Genau in dieser Verschiebbarkeit ' +
        'liegt der eigentliche Mehrwert der Kombination: Die PV-Anlage bekommt einen großen, gut ' +
        'steuerbaren Abnehmer für ihren Strom, statt dass der Überschuss am Mittag für wenig Geld ' +
        'ins Netz eingespeist wird.',
    },
    {
      type: 'heading',
      text: 'Der Effekt auf Ihre Stromkosten',
    },
    {
      type: 'paragraph',
      text:
        'Ohne Wärmepumpe deckt eine typische Einfamilienhaus-PV-Anlage meist nur einen Teil des ' +
        'Jahresstromverbrauchs direkt ab - ein großer Teil der Erzeugung wird tagsüber ins Netz ' +
        'eingespeist, während abends und nachts wieder Netzstrom bezogen wird. Eine Wärmepumpe ' +
        'erhöht den Gesamtverbrauch spürbar, verschiebt aber auch das Verbrauchsprofil: Wird sie ' +
        '(automatisch über eine SG-Ready-Steuerung oder manuell) bevorzugt dann betrieben, wenn die ' +
        'PV-Anlage viel Strom liefert, steigt der Anteil des selbst erzeugten Stroms am ' +
        'Gesamtverbrauch (Eigenverbrauchsquote) deutlich. Jede kWh, die so direkt genutzt statt zu ' +
        'einem niedrigen Einspeisetarif verkauft und später teurer zurückgekauft wird, verbessert ' +
        'die Wirtschaftlichkeit beider Anlagen gemeinsam.',
    },
    {
      type: 'heading',
      text: 'Worauf Sie bei der Kombination achten sollten',
    },
    {
      type: 'list',
      items: [
        'PV-Dimensionierung am tatsächlichen Bedarf ausrichten: Der Jahresstromverbrauch inklusive Wärmepumpe (nicht nur der bisherige Haushaltsverbrauch ohne Heizung) sollte in die Berechnung der Anlagengröße einfließen',
        'Pufferspeicher/thermische Speicherkapazität prüfen: Ein ausreichend großer Pufferspeicher erlaubt es, PV-Überschuss "thermisch" zwischenzuspeichern, statt die Wärmepumpe nur exakt nach momentanem Bedarf laufen zu lassen',
        'Wärmepumpen-Stromtarif vs. Direktverbrauch gegenrechnen: Ein vergünstigter Wärmepumpentarif mit separatem Zähler kann sich lohnen oder auch nicht - das hängt vom Verhältnis aus Eigenverbrauchsanteil und Tarifunterschied ab und ist eine Einzelfallrechnung',
        'Vorlauftemperatur und Wärmeverteilung beachten: Fußbodenheizung erlaubt niedrigere Vorlauftemperaturen und damit eine effizientere Wärmepumpe als klassische Heizkörper - das beeinflusst, wie stark sich die PV-Kombination überhaupt rechnet',
        'Förderprogramme getrennt prüfen: PV-Förderung (siehe unser Beitrag zur Photovoltaik-Förderung 2026) und Wärmepumpen-Förderung (u. a. BAFA/KfW) laufen über unterschiedliche Programme mit eigenen Voraussetzungen - manche Kommunen fördern die Kombination zusätzlich, das ist aber nicht bundesweit einheitlich geregelt',
      ],
    },
    {
      type: 'heading',
      text: 'Reihenfolge: Erst Wärmepumpe, erst PV, oder zusammen planen?',
    },
    {
      type: 'paragraph',
      text:
        'Es gibt keine pauschal richtige Reihenfolge - das hängt vom Sanierungsstand des Gebäudes, ' +
        'der vorhandenen Heizung und dem verfügbaren Budget ab. Wo möglich, lohnt sich aber eine ' +
        'gemeinsame Planung: Die passende PV-Anlagengröße hängt stark davon ab, ob und wie eine ' +
        'Wärmepumpe den Stromverbrauch verändert. Wird zuerst die PV-Anlage ohne Blick auf eine ' +
        'später geplante Wärmepumpe dimensioniert, ist sie im Nachhinein oft zu klein, um einen ' +
        'nennenswerten Teil des neuen Verbrauchs abzudecken.',
    },
    {
      type: 'heading',
      text: 'Wie Roofora hilft',
    },
    {
      type: 'paragraph',
      text:
        'Bei Roofora können Sie Photovoltaik (Komplettsystem, reine Dachmontage oder ' +
        'Elektroinstallation) und Wärmepumpe als eigene, anonymisierte Anfragen stellen und für ' +
        'beide unabhängig voneinander Festpreis-Angebote von geprüften Fachbetrieben vergleichen - ' +
        'ganz ohne Anruf-Flut, mit Kontaktfreigabe erst nach Ihrer aktiven Zustimmung pro Betrieb. ' +
        'So können Sie beide Vorhaben getrennt ausschreiben, aber mit Blick auf die Kombination ' +
        'planen, statt sich vorschnell an einen einzelnen Anbieter für beides zu binden.',
    },
    {
      type: 'heading',
      text: 'Wichtiger Hinweis',
    },
    {
      type: 'paragraph',
      text:
        'Alle Angaben in diesem Beitrag entsprechen dem Stand Juli 2026 und sind allgemeiner Natur - ' +
        'Fördersätze, Stromtarife und die tatsächliche Wirtschaftlichkeit einer Kombination hängen ' +
        'stark vom Einzelfall (Gebäude, Verbrauch, Region) ab. Dieser Beitrag ersetzt keine ' +
        'individuelle energetische oder steuerliche Beratung.',
    },
  ],
  relatedLinks: [
    { label: 'Was kostet eine Solaranlage?', href: '/solaranlage-kosten' },
    { label: 'Photovoltaik Schritt für Schritt planen', href: '/photovoltaik-planen' },
    { label: 'Photovoltaik-Förderung 2026', href: '/blog/photovoltaik-foerderung-2026' },
    { label: 'Photovoltaik und Wärmepumpe: Angebote vergleichen', href: '/photovoltaik-waermepumpe' },
  ],
}
